import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

const KEYWORD = /specifications/i;
const LEADING_PUNCTUATION = /^[\s:.\-–—]+/;

/** Collapsed height (px) before a panel's "Show more" toggle kicks in. */
const COLLAPSED_HEIGHT = 160;

/**
 * Description / Specification tabs, shown below the main product info grid.
 *
 * Two independent content sources, chosen once at render time in
 * blocks/product-tabs.liquid (data-tabs-variant-mode marks which one):
 *
 * 1. Variant mode — the product has real variants, so each tab's content
 *    comes from that variant's own `custom.sunsky_description` /
 *    `custom.sunsky_specification` metafield. All variants' content is
 *    embedded upfront (data-tabs-variant-content) so switching variants
 *    swaps content instantly with no fetch of its own — this listens for
 *    the same ProductSelectEvent every other variant-aware block in this
 *    theme uses (product-price.js, product-sku.js), reading the freshly
 *    resolved variant id off `detail.resource` once the event's promise
 *    settles.
 *
 * 2. Fallback mode — single-variant product (or no metafields set): shows
 *    product.description as-is, then scans it client-side for the word
 *    "specifications" and moves everything from there on into the Specs
 *    tab, so it isn't shown twice.
 *
 * Each panel also gets a "read more" text truncation (see #initReadMore) —
 * capped to COLLAPSED_HEIGHT, toggle only shown if content overflows it.
 */
class ProductTabs extends Component {
  /** @type {(() => void)[]} */
  #readMoreRefreshers = [];

  connectedCallback() {
    super.connectedCallback();
    this.#initTabSwitching();

    if ('tabsVariantMode' in this.dataset) {
      this.#initVariantContent();
    } else {
      this.#splitDescriptionAndSpecs();
    }

    this.#initReadMore();
  }

  #initTabSwitching() {
    const tabBtns = Array.from(this.querySelectorAll('.tab-btn'));
    const tabPanels = Array.from(this.querySelectorAll('.tab-panel'));

    tabBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        tabBtns.forEach((b) => {
          b.classList.remove('active');
          b.setAttribute('aria-selected', 'false');
        });
        tabPanels.forEach((p) => p.classList.remove('active'));

        btn.classList.add('active');
        btn.setAttribute('aria-selected', 'true');

        const panel = this.querySelector(`[data-tab-panel="${btn.dataset.tab}"]`);
        panel?.classList.add('active');
      });
    });
  }

  #initVariantContent() {
    const contentEl = this.querySelector('[data-tabs-variant-content]');
    if (!contentEl) return;

    /** @type {Record<string, {description?: string, specification?: string}>} */
    let variantContent;
    try {
      variantContent = JSON.parse(contentEl.textContent ?? '{}');
    } catch (error) {
      console.warn('[product-tabs] Could not parse variant content JSON:', error);
      return;
    }

    // Shown for any variant without its own description, instead of an empty tab.
    const productDescription = this.querySelector('[data-tabs-product-description]')?.innerHTML ?? '';

    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;

    target.addEventListener(StandardEvents.productSelect, (event) => {
      if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

      event.promise
        .then(({ detail }) => {
          const variantId = detail?.resource?.id;
          if (variantId == null) return;

          const entry = variantContent[variantId];
          if (!entry) return;

          const descriptionEl = this.querySelector('[data-tabs-description-content]');
          const specsEl = this.querySelector('[data-tabs-specs-content]');
          if (descriptionEl) descriptionEl.innerHTML = entry.description || productDescription;
          if (specsEl) specsEl.innerHTML = entry.specification ?? '';

          this.#readMoreRefreshers.forEach((refresh) => refresh());
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[product-tabs] Event promise rejected:', error);
        });
    });
  }

  /**
   * Walks the top-level children of the description block looking for the
   * first one whose text matches /specifications/i. If that node's own text
   * is nothing more than the word itself (plus punctuation — a bare
   * "Specifications" heading/label), the node is dropped entirely.
   * Otherwise the word is cut out of that node's text and everything from
   * there on — the rest of that node's text, plus every sibling that
   * follows — moves into the Specs tab. Only that one matched node is split
   * by string content — deeper nested markup that itself spans the word
   * isn't handled, but product descriptions are normally flat
   * paragraphs/headings, which this covers.
   */
  #splitDescriptionAndSpecs() {
    const descriptionEl = this.querySelector('[data-tabs-description-content]');
    const specsAutoEl = this.querySelector('[data-tabs-specs-auto]');
    if (!descriptionEl || !specsAutoEl) return;

    const children = Array.from(descriptionEl.childNodes);
    let splitNode = null;
    for (const child of children) {
      if (KEYWORD.test(child.textContent || '')) {
        splitNode = child;
        break;
      }
    }
    if (!splitNode) return;

    const ownText = (splitNode.textContent || '').trim();
    const strippedOwnText = ownText.replace(KEYWORD, '').replace(/^[\s:.\-–—]+|[\s:.\-–—]+$/g, '');
    const dropWholeNode = strippedOwnText === '';

    let movedHtml = '';

    if (!dropWholeNode) {
      if (splitNode.nodeType === 1) {
        const html = splitNode.innerHTML;
        const match = KEYWORD.exec(html);
        if (match) {
          const before = html.slice(0, match.index);
          const after = html.slice(match.index).replace(KEYWORD, '').replace(LEADING_PUNCTUATION, '');
          splitNode.innerHTML = before;
          if (after !== '') {
            movedHtml += `<${splitNode.tagName.toLowerCase()}>${after}</${splitNode.tagName.toLowerCase()}>`;
          }
        }
      } else if (splitNode.nodeType === 3) {
        const text = splitNode.textContent ?? '';
        const match = KEYWORD.exec(text);
        if (match) {
          const afterText = text.slice(match.index).replace(KEYWORD, '').replace(LEADING_PUNCTUATION, '');
          splitNode.textContent = text.slice(0, match.index);
          if (afterText !== '') movedHtml += afterText;
        }
      }
    }

    const siblingsAfter = [];
    let sib = splitNode.nextSibling;
    while (sib) {
      siblingsAfter.push(sib);
      sib = sib.nextSibling;
    }
    siblingsAfter.forEach((node) => {
      movedHtml += node.nodeType === 1 ? node.outerHTML : node.textContent;
      node.parentNode?.removeChild(node);
    });

    if (dropWholeNode) {
      descriptionEl.removeChild(splitNode);
    } else if (
      (splitNode.nodeType === 1 && splitNode.innerHTML.trim() === '') ||
      (splitNode.nodeType === 3 && (splitNode.textContent ?? '').trim() === '')
    ) {
      descriptionEl.removeChild(splitNode);
    }

    if (movedHtml.trim() !== '') {
      specsAutoEl.innerHTML = movedHtml + specsAutoEl.innerHTML;
      specsAutoEl.hidden = false;
    }
  }

  /**
   * Plain "read more" text truncation for each panel's .tab-panel__content —
   * clips it to COLLAPSED_HEIGHT and only reveals the toggle if the content
   * actually overflows that height. Reliable here (unlike a fixed-height cap
   * on the variant-picker's button chips elsewhere in this theme) because
   * text height is predictable line-by-line, not dependent on how many
   * variable-width items happen to fit per row.
   */
  #initReadMore() {
    this.querySelectorAll('.tab-panel').forEach((panel) => {
      const content = panel.querySelector('.tab-panel__content');
      const toggle = panel.querySelector('.tab-panel__toggle');
      const toggleText = toggle?.querySelector('.tab-panel__toggle-text');
      if (!(content instanceof HTMLElement) || !(toggle instanceof HTMLButtonElement)) return;

      const refresh = () => {
        const expanded = panel.classList.contains('tab-panel--expanded');

        // Lift the cap before measuring, so scrollHeight reflects the
        // content's true full height rather than last frame's clipped one.
        content.style.maxHeight = '';
        const overflowing = content.scrollHeight > COLLAPSED_HEIGHT + 1;

        panel.classList.toggle('tab-panel--overflowing', overflowing);
        toggle.hidden = !overflowing;

        if (!overflowing) return;

        content.style.maxHeight = expanded ? '' : `${COLLAPSED_HEIGHT}px`;
        if (toggleText) {
          toggleText.textContent = expanded ? toggle.dataset.labelLess ?? '' : toggle.dataset.labelMore ?? '';
        }
        toggle.setAttribute('aria-expanded', String(expanded));
      };

      toggle.addEventListener('click', () => {
        panel.classList.toggle('tab-panel--expanded');
        refresh();
      });

      new ResizeObserver(() => refresh()).observe(content);

      refresh();
      this.#readMoreRefreshers.push(refresh);
    });
  }
}

if (!customElements.get('product-tabs')) {
  customElements.define('product-tabs', ProductTabs);
}
